import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatAccountType } from "../utils/userLabels";
import ThemeSelector from "./ThemeSelector";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const { isAuthenticated, role, user, account_type, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTopTab, setActiveTopTab] = useState("");
  const dashboardRoute = role === "admin" ? "/admin/queue" : "/account";
  const isDashboardRoute =
    location.pathname.startsWith("/account") ||
    location.pathname.startsWith("/admin") ||
    location.pathname === "/post-property" ||
    location.pathname === "/add" ||
    location.pathname === "/choose-role";

  const userInitials = (() => {
    const fullName = String(user?.full_name || "").trim();
    if (!fullName) {
      return "Me";
    }

    const parts = fullName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      const one = parts[0];
      return `${one.charAt(0).toUpperCase()}${one.charAt(1).toUpperCase() || ""}`;
    }

    return `${parts[0].charAt(0).toUpperCase()}${parts[1].charAt(0).toUpperCase()}`;
  })();

  const dashboardLinks = [{ to: "/", label: "Site", match: "site" }];

  useEffect(() => {
    const onDocClick = (event) => {
      if (!menuRef.current) {
        return;
      }
      if (!menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isAuthenticated) {
      if (isDashboardRoute) {
        if (location.pathname === "/post-property" || location.pathname === "/add") {
          setActiveTopTab("post-property");
          return;
        }
        setActiveTopTab("");
        return;
      }
      if (location.pathname === "/rent") {
        setActiveTopTab("rent");
        return;
      }
      if (location.pathname === "/sell") {
        setActiveTopTab("sell");
        return;
      }
      if (location.pathname === "/buy") {
        setActiveTopTab("buy");
        return;
      }
      setActiveTopTab("site");
      return;
    }

    if (location.pathname === "/rent") {
      setActiveTopTab("rent");
      return;
    }
    if (location.pathname === "/sell") {
      setActiveTopTab("sell");
      return;
    }
    if (location.pathname === "/buy") {
      setActiveTopTab("buy");
    }
  }, [isAuthenticated, isDashboardRoute, location.pathname]);

  const onMarketClick = (market) => {
    setActiveTopTab(market);

    if (location.pathname === "/" || location.pathname === "/home") {
      window.dispatchEvent(new CustomEvent("fy-market-action", { detail: { action: market } }));
      return;
    }

    navigate("/", { state: { marketAction: market } });
  };

  const onLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  const openGuestPostProperty = () => {
    setMenuOpen(false);
    navigate("/login", {
      state: {
        from: location.pathname,
        openPostProperty: true,
        listingType: "sale",
        postPropertySource: "site",
        prompt: "Login to continue posting your property."
      }
    });
  };

  const openDashboardPostProperty = () => {
    window.dispatchEvent(new CustomEvent("fy-open-post-property", { detail: { listingType: "sale", source: "dashboard" } }));
    setActiveTopTab("post-property");
  };

  return (
    <header className="fy-header fy-global-header">
      <div className="fy-header-inner">
        <div className="fy-brand-wrap">
          <Link to="/" className="fy-brand-link">
            <div className="fy-brand">FutureYard</div>
          </Link>
          <div className="fy-brand-sub">JAMMU &amp; KASHMIR</div>
        </div>

        <div className="fy-header-right">
          <div className="fy-top-tabs">
            {isAuthenticated && isDashboardRoute ? (
              <>
                {dashboardLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`fy-top-tab fy-top-tab-link ${activeTopTab === link.match ? "active" : ""}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <button
                  type="button"
                  className={`fy-top-tab ${activeTopTab === "post-property" ? "active" : ""}`}
                  onClick={openDashboardPostProperty}
                >
                  Post Property
                </button>
              </>
            ) : isAuthenticated ? (
              <>
                <button type="button" className={`fy-top-tab ${activeTopTab === "buy" ? "active" : ""}`} onClick={() => onMarketClick("buy")}>Buy</button>
                <button type="button" className={`fy-top-tab ${activeTopTab === "sell" ? "active" : ""}`} onClick={() => onMarketClick("sell")}>Sell</button>
                <button type="button" className={`fy-top-tab ${activeTopTab === "rent" ? "active" : ""}`} onClick={() => onMarketClick("rent")}>Rent</button>
                <Link to={dashboardRoute} className="fy-top-tab fy-top-tab-link">Dashboard</Link>
              </>
            ) : (
              <>
                <button type="button" className={`fy-top-tab ${activeTopTab === "buy" ? "active" : ""}`} onClick={() => onMarketClick("buy")}>Buy</button>
                <button type="button" className={`fy-top-tab ${activeTopTab === "sell" ? "active" : ""}`} onClick={() => onMarketClick("sell")}>Sell</button>
                <button type="button" className={`fy-top-tab ${activeTopTab === "rent" ? "active" : ""}`} onClick={() => onMarketClick("rent")}>Rent</button>
              </>
            )}
          </div>

          <div className="fy-menu-wrap" ref={menuRef}>
            <button
              type="button"
              className={isAuthenticated ? `fy-account-trigger ${menuOpen ? "open" : ""}` : `fy-dots-btn ${menuOpen ? "open" : ""}`}
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-haspopup="menu"
              aria-label={isAuthenticated ? "Account menu" : "More options"}
              aria-expanded={menuOpen}
            >
              {isAuthenticated ? (
                <>
                  <span className="fy-account-avatar">{userInitials}</span>
                  <span className={`fy-account-chevron ${menuOpen ? "open" : ""}`} aria-hidden="true">
                    ▾
                  </span>
                </>
              ) : (
                <span className="fy-burger-icon" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              )}
            </button>

            {menuOpen ? (
              <div className="fy-dots-panel" role="menu">
                {!isAuthenticated ? (
                  <>
                    <NavLink to="/login" className="fy-dots-link">Login</NavLink>
                    <NavLink to="/register" className="fy-dots-link">Sign up</NavLink>
                    <button type="button" className="fy-dots-link fy-dots-btn-link" onClick={openGuestPostProperty}>
                      Post Property
                    </button>
                  </>
                ) : (
                  <>
                    <div className="fy-dots-user">
                      <p className="fy-dots-user-name">{user?.full_name || "Member"}</p>
                      <p className="fy-dots-user-email">{user?.email || "-"}</p>
                      <span className="fy-dots-user-role">{formatAccountType(account_type)}</span>
                    </div>
                    <NavLink to="/account/profile" className="fy-dots-link">Profile</NavLink>
                    <div className="fy-dots-theme">
                      <ThemeSelector buttonLabel="Appearance" panelLabel="Appearance" />
                    </div>
                    <button type="button" className="fy-dots-link fy-dots-btn-link" onClick={onLogout}>
                      Logout
                    </button>
                  </>
                )}
                {!isAuthenticated ? (
                  <div className="fy-dots-theme">
                    <ThemeSelector buttonLabel="Appearance" panelLabel="Appearance" />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
